import Drawer from 'components/Drawer';
import { BodySemi, CaptionRegular } from 'components/Text';
import { GOOGLE_METADATA_BY_KEY, POPULARITY_SORT_OPTIONS } from 'utils/metrics';
import {
    filterHealthStats,
    filterNames,
    filterParentName,
} from 'utils/filters';
import { DataTable } from '../DataTable';
import DownloadButton from '../../Button/DownloadButton';
import { IconButton } from '../../Button';
import { OverflowEllipsis } from 'utils/fragments';
import PopularityIndexRelatedEntitiesDrawer from './PopularityIndexRelatedEntitiesDrawer';
import { RelatedHashtagsTableHeadColFilter } from '../DataTable/styledComponents';
import SectionTableFilterChips from '../../SectionTableFilterChips';
import Stack from '../../Stack';
import { createDefaultColumns } from '../DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import useRelatedEntitiesTableProps from './useRelatedEntitiesTableProps.hook';

const ParentEntity = styled(CaptionRegular)`
    max-width: 100%;
    ${OverflowEllipsis};
`;

const relatedHashtagCell = ({ value: hashtag, row }) => {
    return (
        <Stack $space="-.25rem" $direction="column">
            <ParentEntity as="div">{row?.relatedName}</ParentEntity>
            <BodySemi as="div">{hashtag}</BodySemi>
        </Stack>
    );
};

function PopularityIndexRelatedEntitiesTable({
    parentEntity,
    parentEntityId,
    parentEntityType,
    affinityGroups,
    paginateBy,
    brands,
    categories,
    countries,
    week,
    year,
    initialSortOption = POPULARITY_SORT_OPTIONS.popularity_index_avg_12_month,
    initialFilterOption = {},
    icon,
    title,
    subtitle,
    count,
    hideCount = false,
    tools,
    tooltipKey = 'popularityIndexRelatedEntities',
    tooltipExample,
    filename = 'trend table',
    name,
    showParentNameWhenMultiple = false,
    filtersSlot,
    filterParamSeparator,
    filterParamPrefix,
    sortParamKey,
    ...restProps
}) {
    const getColumnsConfig =
        paginateBy === 'brands'
            ? relatedBrandsGetColumnsConfig
            : defaultGetColumnsConfig;
    const handleFilter =
        paginateBy === 'brands'
            ? { healthStats: filterHealthStats, name: filterNames }
            : {
                  healthStats: filterHealthStats,
                  popIndexParentName: filterParentName,
                  name: filterNames,
              };
    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        maxSortMetric,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
    } = useRelatedEntitiesTableProps({
        affinityGroups,
        brands,
        categories,
        countries,
        initialSortOption,
        initialFilterOption,
        getColumnsConfig,
        handleFilter,
        paginateBy,
        name,
        filterParamSeparator,
        filterParamPrefix,
        sortParamKey,
    });
    const parentNameSelectionCount = filters?.popIndexParentName?.length || 0;
    const showParentNameColumn =
        !showParentNameWhenMultiple || parentNameSelectionCount > 1;
    const columnsForDisplay = showParentNameColumn
        ? visibleColumnsConfig
        : visibleColumnsConfig.filter((column) => column.key !== 'parentName');
    const resolvedFiltersSlot =
        typeof filtersSlot === 'function'
            ? filtersSlot({
                  handleFilter,
                  filters,
                  setFilter,
                  setFilters,
                  queryData,
              })
            : filtersSlot;
    const filtersNode = resolvedFiltersSlot || (
        <SectionTableFilterChips
            handleFilter={handleFilter}
            filters={filters}
            setFilter={setFilter}
            setFilters={setFilters}
            queryData={queryData}
            showGroupChips
        />
    );

    return (
        <Stack $space={(theme) => `${theme.space_9}px`}>
            <div
                css={`
                    display: flex;
                    flex-wrap: nowrap;
                    gap: 0.75rem;
                    align-items: flex-start;
                `}
            >
                <div
                    css={`
                        flex-grow: 1;
                    `}
                >
                    {filtersNode}
                </div>
                <Stack
                    $direction="row"
                    css={`
                        display: flex;
                        flex-shrink: 0;
                        margin-left: auto;
                        gap: 0.75rem;
                    `}
                >
                    <DownloadButton
                        ButtonComponent={IconButton}
                        size="default"
                        disabled={!dataReady}
                        isLoading={isLoading}
                        onClick={() => {
                            toast.promise(
                                (async () => {
                                    try {
                                        const columnsConfigCopy =
                                            columnsForDisplay.map((column) => ({
                                                ...column,
                                            }));
                                        const allData = await getQueryData();
                                        downloadItemsAsCSV(
                                            filename,
                                            allData,
                                            columnsConfigCopy,
                                        );
                                    } catch (error) {
                                        console.error(
                                            'Error downloading CSV:',
                                            error,
                                        );
                                        throw error;
                                    }
                                })(),
                                {
                                    loading: 'Preparing download...',
                                    success: 'Download complete!',
                                    error: 'Error downloading CSV',
                                },
                            );
                        }}
                        mixpanelMeta={{ contentKey: 'HashtagDataTable' }}
                    />
                    {columnsMenu}
                </Stack>
            </div>
            <DataTable
                isLoading={isLoading}
                isFetching={isFetching}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                data={sortedData}
                dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                noDataMessage={noDataMessage}
                sortOption={sortOption}
                setSortOption={setSortOption}
                maxSortMetric={maxSortMetric}
                name={name}
                onSearchInputChange={handleSearchInputChange}
                limitSearchWords={3}
                DrawerComponent={Drawer}
                TableHeadColFilter={RelatedHashtagsTableHeadColFilter}
                columns={columnsForDisplay}
                week={week}
                year={year}
                rowHeight={60}
                renderDrawerContent={(selectedId) => (
                    <PopularityIndexRelatedEntitiesDrawer
                        selectedId={selectedId}
                        selectedRow={rows.find((row) => row.id === selectedId)}
                        paginateBy={paginateBy}
                        countryId={countries ? countries[0] : null}
                        parentEntity={parentEntity}
                        parentEntityId={parentEntityId}
                        parentEntityType={parentEntityType}
                        week={week}
                        year={year}
                    />
                )}
                {...restProps}
            />
        </Stack>
    );
}

const defaultGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        ['name', 'parentName', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = relatedHashtagCell;
    return columnsConfig;
};

const relatedBrandsGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns([
        'name',
        GOOGLE_METADATA_BY_KEY.price_segmentation,
        ...visibleOrderedMetrics,
    ]);
    columnsConfig[0].formatCell = relatedHashtagCell;
    return columnsConfig;
};

export default PopularityIndexRelatedEntitiesTable;
